import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useProductPerformance } from '@/hooks/use-product-performance';
import { AlertCircle, DollarSign, Package, TrendingUp } from 'lucide-react';
import { useMemo } from 'react';
import { QuantityChart } from '../product/report/product-report-card';
import { RankingChart } from '../product/report/ranking-report-card';
import { RevenueChart } from '../product/report/revenue-report-card';
import { transformProductData } from './report-utils';

interface ProductReportTabProps {
  fromDate: string;
  toDate: string;
}

export function ProductReportTab({ fromDate, toDate }: ProductReportTabProps) {
  const {
    data: productPerformance,
    isLoading: isLoadingProducts,
    error: productsError,
  } = useProductPerformance(fromDate, toDate, 10);

  // Transform product data for charts
  const productReportData = useMemo(() => {
    if (!productPerformance?.topProductsByRevenue) return [];
    return transformProductData(productPerformance.topProductsByRevenue);
  }, [productPerformance]);

  if (isLoadingProducts) {
    return (
      <>
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader className="space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-3 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-80 w-full" />
          </CardContent>
        </Card>
      </>
    );
  }

  if (productsError) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Error loading product data</h3>
            <p className="text-muted-foreground">
              {productsError instanceof Error
                ? productsError.message
                : 'Failed to load product performance'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!productPerformance) {
    return null;
  }

  return (
    <>
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Products</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {productPerformance.topProductsByRevenue.length}
            </div>
            <p className="text-xs text-muted-foreground">Top products by revenue</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              $
              {productPerformance.topProductsByRevenue
                .reduce((acc, curr) => acc + curr.totalRevenue, 0)
                .toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
            </div>
            <p className="text-xs text-muted-foreground">From top products</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Selling</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold truncate">
              {productPerformance.topProductsByRevenue[0]?.productName || 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">Most popular product</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      {productReportData.length > 0 ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
            <Card className="col-span-4">
              <CardHeader>
                <CardTitle>Sales Quantity</CardTitle>
              </CardHeader>
              <CardContent className="pl-2">
                <QuantityChart data={productReportData} />
              </CardContent>
            </Card>
            <Card className="col-span-3">
              <CardHeader>
                <CardTitle>Revenue Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <RevenueChart data={productReportData} />
              </CardContent>
            </Card>
          </div>

          {/* Ranking and Table */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
            <Card className="col-span-3">
              <CardHeader>
                <CardTitle>Product Ranking</CardTitle>
              </CardHeader>
              <CardContent>
                <RankingChart data={productReportData} />
              </CardContent>
            </Card>
            <Card className="col-span-4">
              <CardHeader>
                <CardTitle>Product Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Profit Margin</TableHead>
                      <TableHead className="text-right">Rank</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productPerformance.topProductsByRevenue.map((product, index) => (
                      <TableRow key={product.productId}>
                        <TableCell className="font-medium">{product.productName}</TableCell>
                        <TableCell className="text-right">{product.totalQuantitySold}</TableCell>
                        <TableCell className="text-right">
                          $
                          {product.totalRevenue.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell className="text-right">
                          {product.profitMarginPercentage.toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right">#{index + 1}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              No product data available for the selected period
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
